'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { CreditCard, DollarSign, AlertCircle, CheckCircle2, Clock, Plus, Search, Download, Send, MoreHorizontal, FileText, XCircle,  } from 'lucide-react';

interface Invoice {
  id: string;
  client: string;
  company: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'draft' | 'cancelled';
  issued: string;
  due: string;
  services: string[];
  avatar: string;
}

const invoices: Invoice[] = [
  { id: 'INV-0094', client: 'James Okafor', company: 'Meridian BPO', amount: 14200, status: 'paid', issued: 'Aug 1, 2024', due: 'Aug 15, 2024', services: ['BPO Center', 'VOIP'], avatar: 'JO' },
  { id: 'INV-0093', client: 'Marcus Webb', company: 'Apex Digital', amount: 9580, status: 'paid', issued: 'Aug 1, 2024', due: 'Aug 15, 2024', services: ['Digital Marketing', 'Web Dev'], avatar: 'MW' },
  { id: 'INV-0092', client: 'Aisha Noor', company: 'Pinnacle Logistics', amount: 4800, status: 'pending', issued: 'Aug 1, 2024', due: 'Aug 20, 2024', services: ['BPO Center'], avatar: 'AN' },
  { id: 'INV-0091', client: 'Priya Sharma', company: 'CloudBridge Inc.', amount: 3400, status: 'overdue', issued: 'Jul 1, 2024', due: 'Jul 20, 2024', services: ['VOIP', 'IT Support'], avatar: 'PS' },
  { id: 'INV-0090', client: 'Tom Hargreaves', company: 'Vantage Media', amount: 1200, status: 'pending', issued: 'Aug 1, 2024', due: 'Aug 25, 2024', services: ['Digital Marketing'], avatar: 'TH' },
  { id: 'INV-0089', client: 'Fatima Al-Rashid', company: 'Gulf Retail Group', amount: 2100, status: 'overdue', issued: 'Jul 15, 2024', due: 'Jul 30, 2024', services: ['Web Dev'], avatar: 'FA' },
  { id: 'INV-0088', client: 'Sarah Mitchell', company: 'NovaTech Solutions', amount: 1800, status: 'cancelled', issued: 'Jun 1, 2024', due: 'Jun 15, 2024', services: ['IT Support'], avatar: 'SM' },
  { id: 'INV-0087', client: 'Marcus Webb', company: 'Apex Digital', amount: 9580, status: 'paid', issued: 'Jul 1, 2024', due: 'Jul 15, 2024', services: ['Digital Marketing', 'Web Dev'], avatar: 'MW' },
  { id: 'INV-0086', client: 'James Okafor', company: 'Meridian BPO', amount: 14200, status: 'paid', issued: 'Jul 1, 2024', due: 'Jul 15, 2024', services: ['BPO Center', 'VOIP'], avatar: 'JO' },
  { id: 'INV-0085', client: 'Aisha Noor', company: 'Pinnacle Logistics', amount: 4800, status: 'draft', issued: 'Aug 10, 2024', due: 'Aug 30, 2024', services: ['BPO Center'], avatar: 'AN' },
];

const statusConfig = {
  paid:      { label: 'Paid',      color: 'text-success',  bg: 'bg-success/10',  icon: CheckCircle2 },
  pending:   { label: 'Pending',   color: 'text-warning',  bg: 'bg-warning/10',  icon: Clock },
  overdue:   { label: 'Overdue',   color: 'text-danger',   bg: 'bg-danger/10',   icon: AlertCircle },
  draft:     { label: 'Draft',     color: 'text-muted-foreground', bg: 'bg-muted', icon: FileText },
  cancelled: { label: 'Cancelled', color: 'text-muted-foreground', bg: 'bg-muted', icon: XCircle },
};

export default function InvoicesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filtered = invoices.filter((inv) => {
    const matchSearch = inv.id.toLowerCase().includes(search.toLowerCase()) || inv.company.toLowerCase().includes(search.toLowerCase()) || inv.client.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalRevenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const pendingAmount = invoices.filter(i => i.status === 'pending').reduce((s, i) => s + i.amount, 0);
  const overdueAmount = invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + i.amount, 0);
  const overdueCount = invoices.filter(i => i.status === 'overdue').length;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">Invoices & Payments</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Track billing, collections, and payment history</p>
          </div>
          <button className="flex items-center gap-2 bg-primary text-primary-foreground px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex-shrink-0">
            <Plus size={16} />
            <span className="hidden sm:inline">New Invoice</span>
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[
            { label: 'Revenue Collected', value: `$${(totalRevenue / 1000).toFixed(1)}k`, sub: 'Paid invoices (all time)', icon: DollarSign, color: 'text-success', bg: 'bg-success/10' },
            { label: 'Pending Payment', value: `$${(pendingAmount / 1000).toFixed(1)}k`, sub: `${invoices.filter(i => i.status === 'pending').length} invoices awaiting`, icon: Clock, color: 'text-warning', bg: 'bg-warning/10' },
            { label: 'Overdue Amount', value: `$${(overdueAmount / 1000).toFixed(1)}k`, sub: `${overdueCount} invoices past due`, icon: AlertCircle, color: 'text-danger', bg: 'bg-danger/10' },
            { label: 'Total Invoices', value: invoices.length, sub: 'Across all clients', icon: CreditCard, color: 'text-primary', bg: 'bg-primary/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className="text-xs sm:text-sm text-muted-foreground">{kpi.label}</span>
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={16} className={kpi.color} />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-foreground">{kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{kpi.sub}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-3 sm:p-4 mb-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by invoice ID, client, or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {(['all', 'paid', 'pending', 'overdue', 'draft', 'cancelled'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground' : 'bg-background border border-border text-muted-foreground hover:text-foreground'}`}
                >
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Invoice</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Services</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Issued</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Due Date</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((inv) => {
                  const sc = statusConfig[inv.status];
                  const StatusIcon = sc.icon;
                  return (
                    <tr key={inv.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-foreground">{inv.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-primary">{inv.avatar}</span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground text-sm">{inv.client}</p>
                            <p className="text-xs text-muted-foreground">{inv.company}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {inv.services.map((s) => (
                            <span key={s} className="text-2xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">{s}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                          <StatusIcon size={11} />{sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-semibold text-foreground">${inv.amount.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{inv.issued}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{inv.due}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><Download size={14} /></button>
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><Send size={14} /></button>
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><MoreHorizontal size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-16 text-center text-muted-foreground text-sm">No invoices match your filters.</div>
            )}
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden flex flex-col gap-3">
          {filtered.map((inv) => {
            const sc = statusConfig[inv.status];
            const StatusIcon = sc.icon;
            return (
              <div key={inv.id} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-primary">{inv.avatar}</span>
                    </div>
                    <div>
                      <p className="font-mono text-sm font-semibold text-foreground">{inv.id}</p>
                      <p className="text-xs text-muted-foreground">{inv.client} · {inv.company}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"><Download size={14} /></button>
                    <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"><MoreHorizontal size={14} /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                    <StatusIcon size={10} />{sc.label}
                  </span>
                  {inv.services.map((s) => (
                    <span key={s} className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">{s}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground text-sm">${inv.amount.toLocaleString()}</span>
                  <span>Issued: {inv.issued}</span>
                  <span>Due: {inv.due}</span>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-16 text-center text-muted-foreground text-sm">No invoices match your filters.</div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
